import { action, computed, makeObservable, observable } from "mobx";
import { UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { DbRecordingEntryWriter } from "../../State/EvidenceDB/DbRecordingEntryWriter";
import { NetworkTrafficRecordingDatabaseState } from '../../State/NetworkTrafficRecordingDatabaseState';
import { SingleOriginFilterState } from "./SingleOriginFilterState";
import { lazyObservable } from "mobx-utils";
import { RecordingSearchState } from "./RecordingSearchState";
import { WindowRoutingState } from "./WindowRoutingState";
import { InstrumentationReportingDatabaseState } from "./InstrumentationReportingDatabaseState";
import { RecordingCookiesSent, RecordingDatabaseCookiesSent } from "./RecordingCookiesSent";
import { NetworkTrafficRecordingState } from "../../State/NetworkTrafficRecordingState";
import { InstrumentationReportingState } from "./InstrumentationReportingState";
import { getTrackingSummaryReportState } from "./getTrackingSummaryReportState";

const TopTabs = ['search', 'reports'] as const;
const TopTabSet = new Set(TopTabs);
type TopTab = NonNullable<typeof TopTabs[number]>;
const isTopTab = (candidate: unknown): candidate is TopTab => TopTabSet.has(candidate as TopTab);

const SearchTabs = ['chronological', 'by search term', 'by destination'] as const;
const SearchTabSet = new Set(SearchTabs);
type SearchTab = NonNullable<typeof SearchTabs[number]>;
const isSearchTab = (candidate: unknown): candidate is SearchTab => SearchTabSet.has(candidate as SearchTab);

const ReportTabs = ['tracking', 'instrumentation'] as const;
const ReportTabSet = new Set(ReportTabs);
type ReportTab = NonNullable<typeof ReportTabs[number]>;
const isReportTab = (candidate: unknown): candidate is ReportTab => ReportTabSet.has(candidate as ReportTab);

export class RecordingViewState {
	readonly networkTrafficRecordingState: NetworkTrafficRecordingState;
	readonly searchState: RecordingSearchState;
	readonly cookiesSent: RecordingCookiesSent;
	readonly instrumentationReportingState: InstrumentationReportingState;
	readonly sourcePageUrlFilterState: SingleOriginFilterState;

	_topTab: TopTab | undefined;
	@computed get topTab(): TopTab { return isTopTab(this._topTab) ? this._topTab : 'search'; }
	setTopTab = action ( (tab: string | undefined) => {
		if (isTopTab(tab)) {
			this._topTab = tab;
		}
	});
	setTopTabFn = (tab: TopTab) => () => this.setTopTab(tab);

	_searchTab: SearchTab | undefined;
	@computed get searchTab(): SearchTab { return isSearchTab(this._searchTab) ? this._searchTab : 'chronological'; }
	setSearchTab = action ( (tab: string | undefined) => {
		if (isSearchTab(tab)) {
			this._searchTab = tab;
		}
	});
	setSearchTabFn = (tab: SearchTab) => () => this.setSearchTab(tab);

	_reportTab: ReportTab | undefined;
	@computed get reportTab(): ReportTab { return isReportTab(this._reportTab) ? this._reportTab : 'tracking'; }
	setReportTab = action ( (tab: string | undefined) => {
		if (isReportTab(tab)) {
			this._reportTab = tab;
		}
	});
	setReportTabFn = (tab: ReportTab) => () => this.setReportTab(tab);

	_objectToDisplayAsJson: unknown | undefined;
	@computed get objectToDisplayAsJson() { return this._objectToDisplayAsJson; }
	setObjectToDisplayAsJson = action ( (json: unknown | undefined) => {
		this._objectToDisplayAsJson = json;
	});

	@computed get recording() { return this.dbRecordingEntryWriter.recording; }
	@computed get recordingUuid() { return this.dbRecordingEntryWriter.recordingUuid; }
	@computed get recordingName() { return this.dbRecordingEntryWriter.name; }
	@computed get recordingNameOrDescription() { return this.dbRecordingEntryWriter.nameOrDescription; }
	setRecordingName = (name: string) => this.dbRecordingEntryWriter.setName(name);

	@computed get recordingStatus() { return this.recording.status; }
	
	@computed get inProgress() {
		return Array.isArray(this.recordingStatus) || this.recordingStatus == 'allTabs';
	}

	@computed get reportBaseName() {
		switch (this.topTab) {
			case 'search':
				switch (this.searchTab) {
					case 'chronological': return 'Chronological Search Report';
					case 'by search term': return 'Search Report By Search Term';
					case 'by destination': return 'Search Report By Destination';
				}
			case 'reports':
				switch (this.reportTab) {
					case 'tracking': return 'Tracking Summary Report';
					case 'instrumentation': return 'Report of Data Listened To';
				}
		}
	}

	@computed get reportFullName() {
		return `${this.recordingNameOrDescription} ${this.reportBaseName}`;
	}

	getDestinationNetworkStatisticsTree = (thirdPartyDestinationsOnly: boolean) =>
		this.networkTrafficRecordingState.destinationNetworkOriginStatisticsTreeFilteredBySourcePage(
			this.sourcePageUrlFilterState.urlFilter, thirdPartyDestinationsOnly);

	@computed get thirdPartyDestinationStatisticsTree() {
		return this.getDestinationNetworkStatisticsTree(true);
	}

	@computed get sourceRegisteredDomainToThirdPartyDestinations() {
		return this.networkTrafficRecordingState.sourceRegisteredDomainToThirdPartyDestinations;
	}

	@computed get trackingSummaryReportState() {
		// const {networkTrafficRecordingState, instrumentationReportingState, searchState, cookiesSent} = this;
		return getTrackingSummaryReportState(this);
		// const {sourceRegisteredDomainToThirdPartyDestinations} = networkTrafficRecordingState;
		// const {searchResultsBySourceRegisteredDomain} = searchState;
		// const searchResultsBySourceRegisteredDomainMap =
		// 	new Map(searchResultsBySourceRegisteredDomain.map( s => [s.sourceRegisteredDomain, s.destinations]));
		// const resultsBySourceRegisteredDomain = sourceRegisteredDomainToThirdPartyDestinations.map( ([sourceRegisteredDomain, destinations]) => {
		// 	const searchResultsByDestination = new Map(
		// 		(searchResultsBySourceRegisteredDomainMap.get(sourceRegisteredDomain) ?? []).map( d => ([d.destinationRegisteredDomain, d])));
		// 	const resultsByDestinationRegisteredDomain = [...destinations.values()].map( (destination) => {
		// 		const {registeredDomain: destinationRegisteredDomain, domains} = destination;
		// 		const cookiesByName = this.cookiesSent.cookiesByRegisteredDomain.get(sourceRegisteredDomain)?.get(destinationRegisteredDomain);
		// 		const cookies = [...(cookiesByName?.values() ?? [])];
		// 		const searchResults = searchResultsByDestination.get(destinationRegisteredDomain);
		// 		const {sourceRegDomainToCodeRegDomainToAPIsAccessed, sourceRegDomainToCodeRegDomainToListener} = instrumentationReportingState;
		// 		const apisAccessed = sourceRegDomainToCodeRegDomainToAPIsAccessed.get(sourceRegisteredDomain)?.get(destinationRegisteredDomain);
		// 		const listeners = sourceRegDomainToCodeRegDomainToListener.get(sourceRegisteredDomain)?.get(destinationRegisteredDomain);
		// 		const destinationDomains = [...domains.values()];
		// 		let knownTracker: KnownTracker | undefined = getKnownTracker(destinationRegisteredDomain);
		// 		if (knownTracker == null) {
		// 			destinationDomains.find( d => (knownTracker = getKnownTracker(d.domain)) != null);
		// 		}

		// 		return {destinationRegisteredDomain, cookies, searchResults,
		// 			listeners, apisAccessed, destinationDomains, knownTracker};
		// 	}).sort( (a, b) => (a.knownTracker?.organizationKnownAs ?? a.destinationRegisteredDomain).localeCompare(
		// 		b.knownTracker?.organizationKnownAs ?? b.destinationRegisteredDomain));
		// 	return {sourceRegisteredDomain, resultsByDestinationRegisteredDomain};
		// }).sort( (a, b) => b.resultsByDestinationRegisteredDomain.length - a.resultsByDestinationRegisteredDomain.length);
		// return resultsBySourceRegisteredDomain;
	}


	constructor(public readonly windowRoutingState: WindowRoutingState, readonly dbRecordingEntryWriter: DbRecordingEntryWriter, readonly searchWorkerUrl?: string) {
 		this.networkTrafficRecordingState = NetworkTrafficRecordingDatabaseState.forRecordingUuid(dbRecordingEntryWriter.recordingUuid);
		this.sourcePageUrlFilterState = new SingleOriginFilterState(
			() => this.networkTrafficRecordingState.sourcePageOriginStatisticsTree
		);
		this.searchState = new RecordingSearchState(this);
		this.instrumentationReportingState = new InstrumentationReportingDatabaseState(this.recordingUuid);
		this.cookiesSent = new RecordingDatabaseCookiesSent(this.recordingUuid);
		makeObservable(this, {
			_topTab: observable, _searchTab: observable, _reportTab: observable, _objectToDisplayAsJson: observable
		});
	}

	static #forRecordingUuid = new Map<UUID, RecordingViewState>();
	static forRecordingUuidAsync = async (windowRoutingState: WindowRoutingState, recordingUuid: UUID) => {
		const existingRecordingViewState = RecordingViewState.#forRecordingUuid.get(recordingUuid)
		if (existingRecordingViewState != null) {
			return existingRecordingViewState;
		}
		const dbRecordingEntryWriter = await DbRecordingEntryWriter.fromRecordingUuid(recordingUuid);
		if (dbRecordingEntryWriter == null) { 
			throw new Error(`No recording found for recordingUuid ${recordingUuid}`);
		}
		const recordingViewState = new RecordingViewState(windowRoutingState, dbRecordingEntryWriter);
		RecordingViewState.#forRecordingUuid.set(recordingUuid, recordingViewState);
		return recordingViewState
	}

	static forRecordingUuid(windowRoutingState: WindowRoutingState, recordingUuid: UUID | undefined ) {
		if (recordingUuid == null) return undefined;
		return RecordingViewState.#forRecordingUuid.get(recordingUuid) ??
			lazyObservable<RecordingViewState | undefined>( async (sink) => {
				try {
					sink(await RecordingViewState.forRecordingUuidAsync(windowRoutingState, recordingUuid));
				} catch (e) {
					console.error(e);
					sink(undefined);
				}
			}).current();
	}
}

